import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { UserPlus, Trash2, Mail, Shield, Clock, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_LABELS = { owner: 'Owner', studio_manager: 'Studio Manager', event_operator: 'Event Operator' };
const ROLE_DESC = {
  owner: 'Full access, including revenue and team management.',
  studio_manager: 'Manages classes & events, verifies payments, views rosters — no revenue totals.',
  event_operator: 'QR check-in & event guest list only. Access can auto-expire.',
};

const TeamManagement = () => {
  const [users, setUsers] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: '', name: '', role: 'studio_manager', access_expires_at: '' });

  useEffect(() => { fetchUsers(); }, []);

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } });

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, authHeaders());
      setUsers(res.data);
    } catch (error) {
      toast.error('Failed to load team');
    }
  };

  const toLocalInput = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const openAdd = () => { setEditing(null); setForm({ email: '', name: '', role: 'studio_manager', access_expires_at: '' }); setIsOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ email: u.email, name: u.name || '', role: u.role, access_expires_at: toLocalInput(u.access_expires_at) }); setIsOpen(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const expiry = form.access_expires_at ? new Date(form.access_expires_at).toISOString() : null;
    try {
      if (editing) {
        await axios.put(`${API}/admin/users/${editing.id}`, { name: form.name, role: form.role, access_expires_at: expiry }, authHeaders());
        toast.success('Team member updated');
      } else {
        await axios.post(`${API}/admin/users`, { email: form.email, name: form.name, role: form.role, access_expires_at: expiry }, authHeaders());
        toast.success('Invite sent! They will receive a set-password email.');
      }
      fetchUsers();
      setIsOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Remove ${u.name || u.email} from the team?`)) return;
    try {
      await axios.delete(`${API}/admin/users/${u.id}`, authHeaders());
      toast.success('Team member removed');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove');
    }
  };

  const resend = async (u) => {
    try {
      await axios.post(`${API}/admin/users/${u.id}/resend-invite`, {}, authHeaders());
      toast.success('Set-password invite re-sent');
    } catch (error) {
      toast.error('Failed to resend invite');
    }
  };

  const expiryBadge = (u) => {
    if (!u.access_expires_at) return null;
    const expired = new Date(u.access_expires_at) < new Date();
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 ${expired ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700'}`}>
        <Clock size={12} /> {expired ? 'Expired' : 'Expires'} {new Date(u.access_expires_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
      </span>
    );
  };

  return (
    <div className="p-8" data-testid="team-management">
      <div className="flex flex-wrap justify-between items-center gap-4 mb-2">
        <h2 className="text-3xl font-bold flex items-center gap-2" style={{ fontFamily: 'var(--heading-font)' }}>
          <Shield size={28} /> Team & Access
        </h2>
        <button onClick={openAdd} className="bg-[var(--accent)] text-black hover:bg-[var(--accent-hover)] rounded-full px-6 py-3 font-bold transition-transform hover:scale-105 active:scale-95 flex items-center gap-2" data-testid="add-user-btn">
          <UserPlus size={20} /> ADD TEAM MEMBER
        </button>
      </div>
      <p className="text-sm text-zinc-500 mb-8">Owners have full access. New members receive an email to set their own password.</p>

      <div className="overflow-x-auto border border-zinc-200 rounded bg-white">
        <table className="w-full">
          <thead className="bg-zinc-100">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-bold">Name</th>
              <th className="px-4 py-3 text-left text-sm font-bold">Email</th>
              <th className="px-4 py-3 text-left text-sm font-bold">Role</th>
              <th className="px-4 py-3 text-left text-sm font-bold">Access</th>
              <th className="px-4 py-3 text-right text-sm font-bold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-zinc-200" data-testid={`team-row-${u.email}`}>
                <td className="px-4 py-3 font-medium">{u.name || '—'}</td>
                <td className="px-4 py-3 text-sm text-zinc-600">{u.email}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${u.role === 'owner' ? 'bg-black text-white' : u.role === 'studio_manager' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                    {ROLE_LABELS[u.role] || u.role}
                  </span>
                </td>
                <td className="px-4 py-3">{expiryBadge(u) || <span className="text-xs text-zinc-400">No expiry</span>}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => resend(u)} title="Resend invite" className="p-2 text-zinc-500 hover:bg-zinc-100 rounded" data-testid={`resend-invite-${u.email}`}><Mail size={16} /></button>
                  <button onClick={() => openEdit(u)} title="Edit" className="p-2 text-zinc-600 hover:bg-zinc-100 rounded" data-testid={`edit-user-${u.email}`}><Pencil size={16} /></button>
                  {u.role !== 'owner' && (
                    <button onClick={() => handleDelete(u)} title="Remove" className="p-2 text-red-600 hover:bg-red-100 rounded" data-testid={`delete-user-${u.email}`}><Trash2 size={16} /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-md" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'var(--heading-font)' }}>
              {editing ? 'Edit Team Member' : 'Add Team Member'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="u-name">Name</Label>
              <Input id="u-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-form-name" />
            </div>
            <div>
              <Label htmlFor="u-email">Email *</Label>
              <Input id="u-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required disabled={!!editing} data-testid="user-form-email" />
            </div>
            <div>
              <Label>Role *</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="owner">Owner</SelectItem>
                  <SelectItem value="studio_manager">Studio Manager</SelectItem>
                  <SelectItem value="event_operator">Event Operator</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-zinc-500 mt-1">{ROLE_DESC[form.role]}</p>
            </div>
            {form.role === 'event_operator' && (
              <div>
                <Label htmlFor="u-expiry">Access Expires (e.g. event end)</Label>
                <Input id="u-expiry" type="datetime-local" value={form.access_expires_at} onChange={(e) => setForm({ ...form, access_expires_at: e.target.value })} data-testid="user-form-expiry" />
                <p className="text-xs text-zinc-500 mt-1">After this time the operator can no longer log in. Leave blank for no expiry.</p>
              </div>
            )}
            <button type="submit" className="w-full bg-[var(--accent)] text-black hover:bg-[var(--accent-hover)] rounded-full px-6 py-3 font-bold transition-transform hover:scale-105 active:scale-95" data-testid="user-form-submit">
              {editing ? 'SAVE CHANGES' : 'SEND INVITE'}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamManagement;
